import * as React from 'react';
import {
  Switch as RACSwitch,
  type SwitchProps as RACSwitchProps,
  type SwitchRenderProps,
} from 'react-aria-components';

import { cn } from '../lib/cn';

/**
 * Klaret Switch (handoff §10.3).
 * 36×20 track, r-full. Off = --tvl-inset, On = --tvl-accent.
 * Thumb: 16px --tvl-raised circle with --tvl-e1, 2px inset travel.
 * Instant-effect settings only; forms use Checkbox.
 * Focus = global 2px ink outline (globals.css) — no accent ring.
 */
export interface SwitchProps extends Omit<RACSwitchProps, 'children'> {
  className?: string;
  children?: React.ReactNode;
}

export const Switch = React.forwardRef<HTMLLabelElement, SwitchProps>(
  ({ className, children, ...props }, ref) => (
    <RACSwitch
      ref={ref}
      className={cn('group inline-flex cursor-pointer items-center gap-2', className)}
      {...props}
    >
      {({ isSelected, isDisabled }: SwitchRenderProps) => (
        <>
          {/* track */}
          <span
            className={cn(
              'relative inline-flex h-5 w-9 shrink-0 items-center rounded-[var(--tvl-r-full)]',
              'transition-colors duration-[var(--tvl-d-fast)] ease-[var(--tvl-ease-out)]',
              isSelected ? 'bg-[var(--tvl-accent)]' : 'bg-[var(--tvl-inset)]',
              isDisabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {/* thumb */}
            <span
              className={cn(
                'absolute inline-block h-4 w-4 rounded-[var(--tvl-r-full)] bg-[var(--tvl-raised)]',
                'shadow-[var(--tvl-e1)]',
                'transition-transform duration-[var(--tvl-d-fast)] ease-[var(--tvl-ease-out)]',
                // 2px inset off-side → 2px inset on-side (track 36px, thumb 16px → travel 16px)
                isSelected ? 'translate-x-[18px]' : 'translate-x-[2px]',
              )}
            />
          </span>
          {/* label */}
          {children ? (
            <span
              className={cn(
                'text-sm leading-5 text-[var(--tvl-ink)]',
                isDisabled && 'text-[var(--tvl-ink-disabled)]',
              )}
            >
              {children}
            </span>
          ) : null}
        </>
      )}
    </RACSwitch>
  ),
);
Switch.displayName = 'Switch';
